import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Card,
  CardBody,
  SimpleGrid,
  Divider,
  Badge,
  IconButton,
  Tooltip,
  useToast,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiXCircle,
  FiCopy,
  FiArrowLeft,
  FiRefreshCw,
  FiInfo,
} from "react-icons/fi";
import { useGetPaymentStatus, useVerifyAndUpdateFunding } from "../../../services/payrollService";
import { useGetPlanInfo } from "../../../services/planService";
import { formatCurrency } from "../../../utils/currency";
import { showSuccessAlert, showErrorAlert } from "../../../utils/alert";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  
  const status = searchParams.get("status");
  const txRef = searchParams.get("tx_ref") || sessionStorage.getItem("pending_tx_ref") || localStorage.getItem("pending_tx_ref_backup");
  const transactionId = searchParams.get("transaction_id");
  
  const [verificationStatus, setVerificationStatus] = useState("checking"); // 'checking', 'completed', 'successful', 'pending', 'failed', 'error'
  const [paymentData, setPaymentData] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const pollingIntervalRef = useRef(null);
  const pollingStartTimeRef = useRef(null);
  
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const { planInfo } = useGetPlanInfo();
  
  // Validate tx_ref format
  const validateTxRef = (ref) => {
    if (!ref) return false;
    const txRefPattern = /^WALLET-[a-f0-9]+-[\d.]+$/;
    return txRefPattern.test(ref);
  };

  // Check payment status - enabled regardless of URL status param
  const { data: paymentStatusData, isLoading: paymentStatusLoading, error: paymentStatusError, refetch: refetchPaymentStatus } = useGetPaymentStatus(
    txRef,
    { enabled: !!txRef && validateTxRef(txRef) }
  );

  // Verify and update funding mutation
  const verifyMutation = useVerifyAndUpdateFunding();


  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingStartTimeRef.current = null;
    setPollAttempts(0);
  }, []);

  // Start polling for pending payments
  useEffect(() => {
    if (verificationStatus === "pending" && txRef && validateTxRef(txRef) && !pollingIntervalRef.current) {
      if (!pollingStartTimeRef.current) {
        pollingStartTimeRef.current = Date.now();
      }

      const POLLING_INTERVAL = 4000; // 4 seconds (between 3-5 seconds)
      const MAX_POLLING_TIME = 30000; // 30 seconds max
      const MAX_ATTEMPTS = 8; // ~30 seconds (8 attempts * 4 seconds = 32 seconds, but we check time too)

      pollingIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - (pollingStartTimeRef.current || Date.now());
        setPollAttempts(prev => {
          const newAttempts = prev + 1;
          
          // Check if we've exceeded time or attempts
          if (elapsed >= MAX_POLLING_TIME || newAttempts >= MAX_ATTEMPTS) {
            stopPolling();
            setVerificationStatus("error");
            toast({
              title: "Payment Verification Timeout",
              description: "Payment is still being processed. Please check your wallet balance manually or contact support.",
              status: "warning",
              duration: 8000,
              isClosable: true,
            });
            return newAttempts;
          }
          
          // Continue polling
          refetchPaymentStatus();
          return newAttempts;
        });
      }, POLLING_INTERVAL);

      return () => {
        stopPolling();
      };
    }
  }, [verificationStatus, txRef, refetchPaymentStatus, stopPolling, toast]);

  // Process payment status response
  useEffect(() => {
    if (paymentStatusData && !paymentStatusLoading) {
      // Stop polling if we have a definitive status
      if (paymentStatusData.status === "completed" || paymentStatusData.status === "successful" || paymentStatusData.status === "failed") {
        stopPolling();
      }

      if (paymentStatusData.status === "completed") {
        // Already processed by webhook
        setVerificationStatus("completed");
        setPaymentData(paymentStatusData);
        // Clear stored tx_ref
        sessionStorage.removeItem("pending_tx_ref");
        localStorage.removeItem("pending_tx_ref_backup");
        
        // Show appropriate message based on instant_availability
        if (paymentStatusData.instant_availability) {
          toast({
            title: "✅ Funds Available Immediately!",
            description: "Your wallet has been credited and funds are available for payroll processing now.",
            status: "success",
            duration: 8000,
            isClosable: true,
          });
        } else {
          toast({
            title: "✅ Funding Successful!",
            description: "Your wallet has been successfully funded.",
            status: "success",
            duration: 8000,
            isClosable: true,
          });
        }
      } else if (paymentStatusData.status === "successful") {
        // Verified but not processed yet - call verify-and-update-funding
        setVerificationStatus("processing");
        verifyMutation.mutate(
          { tx_ref: txRef },
          {
            onSuccess: (data) => {
              setVerificationStatus("completed");
              setPaymentData(data);
              // Clear stored tx_ref
              sessionStorage.removeItem("pending_tx_ref");
              localStorage.removeItem("pending_tx_ref_backup");
              
              // Show appropriate message based on instant_availability
              if (data.instant_availability) {
                toast({
                  title: "✅ Funds Available Immediately!",
                  description: "Your wallet has been credited and funds are available for payroll processing now.",
                  status: "success",
                  duration: 8000,
                  isClosable: true,
                });
              } else {
                toast({
                  title: "✅ Funding Successful!",
                  description: "Your wallet has been successfully funded.",
                  status: "success",
                  duration: 8000,
                  isClosable: true,
                });
              }
            },
            onError: (error) => {
              setVerificationStatus("error");
              console.error("Verification failed:", error);
            },
          }
        );
      } else if (paymentStatusData.status === "pending") {
        // Payment is pending - start polling
        if (verificationStatus !== "pending") {
          setVerificationStatus("pending");
        }
        setPaymentData(paymentStatusData);
      } else {
        // Failed or unknown status
        setVerificationStatus("failed");
        setPaymentData(paymentStatusData);
      }
    }
  }, [paymentStatusData, paymentStatusLoading]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Handle errors
  useEffect(() => {
    if (paymentStatusError) {
      setVerificationStatus("error");
    }
  }, [paymentStatusError]);

  // Initial validation - check payment status immediately on mount
  useEffect(() => {
    if (!txRef || !validateTxRef(txRef)) {
      setVerificationStatus("error");
      showErrorAlert("Invalid transaction reference. Please check your payment history or contact support.");
    } else {
      // Trigger initial payment status check
      refetchPaymentStatus();
    }
  }, [txRef]);

  const handleReturnToWallet = () => {
    navigate("/payroll/wallet");
  };

  const handleCopyTransactionRef = () => {
    const ref = paymentData?.transaction?.reference || paymentData?.transaction_reference || txRef;
    if (ref) {
      navigator.clipboard.writeText(ref);
      toast({
        title: "Copied!",
        description: "Transaction reference copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleRetryVerification = () => {
    if (txRef) {
      refetchPaymentStatus();
    }
  };

  // Loading state
  if (verificationStatus === "checking" || paymentStatusLoading || verificationStatus === "processing") {
    return (
      <Box w="100%" bg={bgColor} minH="100vh" p={6}>
        <Card maxW="800px" mx="auto" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody p={8}>
            <VStack spacing={6} align="center">
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <VStack spacing={2} align="center">
                <Heading size="md" color="gray.700">
                  {verificationStatus === "processing" ? "Processing Payment..." : "Verifying Payment..."}
                </Heading>
                <Text color="gray.500" fontSize="sm">
                  Please wait while we verify your transaction
                </Text>
              </VStack>
              <Box w="100%" mt={4}>
                <Skeleton height="20px" mb={2} />
                <SkeletonText mt="4" noOfLines={4} spacing="4" />
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  // Pending state with polling
  if (verificationStatus === "pending") {
    return (
      <Box w="100%" bg={bgColor} minH="100vh" p={6}>
        <Card maxW="800px" mx="auto" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody p={8}>
            <VStack spacing={6} align="center">
              <Spinner size="xl" color="orange.500" thickness="4px" />
              <VStack spacing={2} align="center">
                <Heading size="md" color="gray.700">
                  Payment Pending
                </Heading>
                <Text color="gray.500" fontSize="sm" textAlign="center">
                  Your payment is being processed. We're checking the status automatically...
                </Text>
                {pollAttempts > 0 && (
                  <Text color="gray.400" fontSize="xs">
                    Attempt {pollAttempts} of 8 (max 30 seconds)
                  </Text>
                )}
              </VStack>
              <Alert status="info" borderRadius="md" w="100%">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Payment verification is in progress. This usually takes a few seconds. Please wait...
                </AlertDescription>
              </Alert>
              <Button
                leftIcon={<FiArrowLeft />}
                variant="outline"
                onClick={handleReturnToWallet}
                size="md"
              >
                Return to Wallet
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  // Error state
  if (verificationStatus === "error" || !txRef || !validateTxRef(txRef)) {
    return (
      <Box w="100%" bg={bgColor} minH="100vh" p={6}>
        <Card maxW="800px" mx="auto" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody p={8}>
            <VStack spacing={6} align="center">
              <Box color="red.500">
                <FiXCircle size={64} />
              </Box>
              <VStack spacing={2} align="center">
                <Heading size="lg" color="red.600">
                  Unable to Verify Payment
                </Heading>
                <Text color="gray.600" textAlign="center">
                  We couldn't verify your payment transaction. This might be due to:
                </Text>
                <VStack align="start" spacing={1} mt={2}>
                  <Text fontSize="sm" color="gray.600">• Invalid transaction reference</Text>
                  <Text fontSize="sm" color="gray.600">• Network connectivity issues</Text>
                  <Text fontSize="sm" color="gray.600">• Payment is still being processed</Text>
                </VStack>
              </VStack>
              <HStack spacing={4} mt={4}>
                <Button
                  leftIcon={<FiRefreshCw />}
                  colorScheme="blue"
                  onClick={handleRetryVerification}
                >
                  Retry Verification
                </Button>
                <Button
                  leftIcon={<FiArrowLeft />}
                  variant="outline"
                  onClick={handleReturnToWallet}
                >
                  Return to Wallet
                </Button>
              </HStack>
              {txRef && (
                <Box w="100%" mt={4} p={4} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Transaction Reference:
                  </Text>
                  <HStack>
                    <Text fontFamily="mono" fontSize="sm" color="gray.800">
                      {txRef}
                    </Text>
                    <IconButton
                      icon={<FiCopy />}
                      size="sm"
                      variant="ghost"
                      onClick={handleCopyTransactionRef}
                      aria-label="Copy transaction reference"
                    />
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Keep this reference and contact support if the issue persists
                  </Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  // Failed payment
  if (verificationStatus === "failed") {
    return (
      <Box w="100%" bg={bgColor} minH="100vh" p={6}>
        <Card maxW="800px" mx="auto" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody p={8}>
            <VStack spacing={6} align="center">
              <Box color="red.500">
                <FiXCircle size={64} />
              </Box>
              <VStack spacing={2} align="center">
                <Heading size="lg" color="red.600">
                  Payment Failed
                </Heading>
                <Text color="gray.600" textAlign="center">
                  {paymentData?.message || "Your payment could not be completed. Please try again."}
                </Text>
              </VStack>
              <Button
                leftIcon={<FiArrowLeft />}
                colorScheme="blue"
                onClick={handleReturnToWallet}
                size="lg"
              >
                Return to Wallet
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  // Success state
  if (verificationStatus === "completed" && paymentData) {
    return (
      <Box w="100%" bg={bgColor} minH="100vh" p={6}>
        <Card maxW="800px" mx="auto" bg={cardBg} border="1px solid" borderColor={borderColor} shadow="lg">
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              {/* Success Header */}
              <VStack spacing={4} align="center">
                <Box color="green.500">
                  <FiCheckCircle size={64} />
                </Box>
                <VStack spacing={2} align="center">
                  <Heading size="lg" color="green.600">
                    Payment Successful!
                  </Heading>
                  <Text color="gray.600" textAlign="center">
                    Your wallet has been successfully funded
                  </Text>
                </VStack>
              </VStack>

              <Divider />

              {/* Transaction Details */}
              <Box>
                <Heading size="sm" color="gray.700" mb={4}>
                  Transaction Details
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Amount Paid
                    </Text>
                    <Text fontSize="xl" fontWeight="700" color="blue.600">
                      {formatCurrency(
                        paymentData.transaction?.amount_paid || paymentData.amount_charged || 0, 
                        planInfo?.country
                      )}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      Currency
                    </Text>
                    <Badge colorScheme="blue" fontSize="md" p={2}>
                      {paymentData.transaction?.currency || paymentData.currency || "NGN"}
                    </Badge>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Fees Breakdown */}
              {(paymentData.transaction?.fees || paymentData.fees) && (
                <Box bg="orange.50" p={4} borderRadius="lg" border="1px solid" borderColor="orange.200">
                  <HStack spacing={2} mb={3}>
                    <FiInfo color="orange.600" />
                    <Heading size="sm" color="orange.800">
                      Transaction Fees
                    </Heading>
                  </HStack>
                  <VStack spacing={2} align="stretch" pl={6}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="orange.700">Transaction Fee:</Text>
                      <Text fontSize="sm" fontWeight="600" color="orange.800">
                        {formatCurrency(
                          paymentData.transaction?.fees?.transaction_fee || paymentData.fees?.transaction_fee || 0, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="orange.700">VAT:</Text>
                      <Text fontSize="sm" fontWeight="600" color="orange.800">
                        {formatCurrency(
                          paymentData.transaction?.fees?.vat || paymentData.fees?.vat || 0, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                    <Divider borderColor="orange.300" />
                    <HStack justify="space-between">
                      <Text fontWeight="700" color="orange.800">Total Fees:</Text>
                      <Text fontWeight="700" fontSize="lg" color="orange.900">
                        {formatCurrency(
                          paymentData.transaction?.fees?.total || paymentData.fees?.total || 0, 
                          planInfo?.country
                        )}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              )}

              {/* Amount Added to Wallet */}
              <Box bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="600" color="green.800" fontSize="lg">
                    Amount Added to Wallet:
                  </Text>
                  <Text fontWeight="700" fontSize="2xl" color="green.700">
                    {formatCurrency(
                      paymentData.transaction?.amount_added || paymentData.amount_settled || 0, 
                      planInfo?.country
                    )}
                  </Text>
                </HStack>
                <Text fontSize="xs" color="green.700">
                  This is the actual amount added after fees
                </Text>
                {/* Show availability message from transaction object if available */}
                {paymentData.transaction?.availability_message ? (
                  <Alert 
                    status={paymentData.transaction?.availability_status === "available_immediately" ? "success" : "info"} 
                    borderRadius="md" 
                    mt={3}
                    fontSize="sm"
                  >
                    <AlertIcon />
                    <AlertDescription>
                      {paymentData.transaction.availability_message}
                    </AlertDescription>
                  </Alert>
                ) : paymentData.instant_availability !== undefined && paymentData.instant_availability && (
                  <Alert 
                    status="success" 
                    borderRadius="md" 
                    mt={3}
                    fontSize="sm"
                  >
                    <AlertIcon />
                    <AlertDescription>
                      ✅ Funds are available immediately! You can process payroll now.
                    </AlertDescription>
                  </Alert>
                )}
              </Box>


              {/* Transaction Summary - Focus on this transaction only */}
              <Box>
                <Heading size="sm" color="gray.700" mb={3}>
                  Transaction Summary
                </Heading>
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Amount Funded:</Text>
                    <Text fontSize="md" fontWeight="600" color="green.600">
                      {formatCurrency(
                        paymentData.transaction?.amount_added || paymentData.amount_settled || 0, 
                        planInfo?.country
                      )}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">Status:</Text>
                    <Badge 
                      colorScheme={
                        paymentData.transaction?.availability_status === "available_immediately" ||
                        paymentData.availability_status === "available_immediately" ||
                        paymentData.funds_status === "available_immediately" ||
                        paymentData.instant_availability === true
                          ? "green" 
                          : "orange"
                      }
                      fontSize="sm"
                    >
                      {paymentData.transaction?.availability_status === "available_immediately" ||
                       paymentData.availability_status === "available_immediately" ||
                       paymentData.funds_status === "available_immediately" ||
                       paymentData.instant_availability === true
                        ? "✅ Available Immediately"
                        : "⏳ Pending Settlement"}
                    </Badge>
                  </HStack>
                  {(paymentData.transaction?.availability_status === "pending_settlement" ||
                    paymentData.availability_status === "pending_settlement" ||
                    paymentData.funds_status === "pending_settlement" ||
                    paymentData.instant_availability === false) && (
                    <Alert status="info" borderRadius="md" fontSize="xs">
                      <AlertIcon />
                      <AlertDescription>
                        {paymentData.transaction?.availability_message ||
                         paymentData.availability_message ||
                         "This amount will be available in 1-5 business days once settlement completes."}
                      </AlertDescription>
                    </Alert>
                  )}
                </VStack>
              </Box>

              {/* Transaction Reference */}
              {(paymentData.transaction?.reference || paymentData.transaction_reference || txRef) && (
                <Box p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor={borderColor}>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="gray.600" fontWeight="500">
                        Transaction Reference
                      </Text>
                      <Text fontFamily="mono" fontSize="sm" color="gray.800">
                        {paymentData.transaction?.reference || paymentData.transaction_reference || txRef}
                      </Text>
                      {(paymentData.transaction?.flw_ref || paymentData.flw_ref) && (
                        <Text fontSize="xs" color="gray.500">
                          Payment Provider Ref: {paymentData.transaction?.flw_ref || paymentData.flw_ref}
                        </Text>
                      )}
                    </VStack>
                    <Tooltip label="Copy transaction reference">
                      <IconButton
                        icon={<FiCopy />}
                        size="sm"
                        variant="ghost"
                        onClick={handleCopyTransactionRef}
                        aria-label="Copy transaction reference"
                      />
                    </Tooltip>
                  </HStack>
                </Box>
              )}

              <Divider />

              {/* Action Buttons */}
              <HStack spacing={4} justify="center">
                <Button
                  leftIcon={<FiArrowLeft />}
                  colorScheme="blue"
                  size="lg"
                  onClick={handleReturnToWallet}
                >
                  Return to Wallet
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/payroll/wallet")}
                >
                  View Transaction History
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </Box>
    );
  }

  return null;
};

export default PaymentSuccess;

